import * as assert from 'assert';
import { computed, observable } from 'mobx';
import * as moment from 'moment';
import * as _ from 'underscore';
import { Artifact } from './Artifact';
import { Family } from './Family';
import { ISimplifiedTag } from './ITag';

export interface INewCollection {
    title: string;
}

interface IArtifactInfo {
    artifact_id: number;
    added_at: string;
    user_id?: number;
}

export interface IServerCollection extends INewCollection {
    id: number;
    user_id: number;
    created_at: string | moment.Moment;
    artifacts_info: Array<IArtifactInfo>;
}

export class ServerCollection {
    public id: number;
    public title: string;
    public user_id: number;
    public artifacts_info: Array<IArtifactInfo>;
    public created_at: moment.Moment;

    protected artifactInfoMap: Map<number, IArtifactInfo> = new Map();

    public constructor(args: IServerCollection) {
        assert(args);
        assert(typeof args.id === 'number', 'invalid collection id');
        assert(typeof args.title === 'string', 'invalid collection title');
        assert(typeof args.user_id === 'number', 'invalid user_id on collection');
        assert(moment.isMoment(args.created_at) || typeof args.created_at === 'string', 'invalid created_at formatting on collection');

        this.id = args.id;
        this.title = args.title;
        this.user_id = args.user_id;
        this.artifacts_info = args.artifacts_info;
        this.created_at = moment(args.created_at);
    }

    @computed
    get artifact_ids(): Array<number> {
        return this.artifacts_info.map(info => info.artifact_id);
    }
}

const FALLBACK_COLLECTION_IMG = 'http://via.placeholder.com/99x99';

interface ITagTitlesWithCount {
    tag: string;
    count: number;
}

export class Collection extends ServerCollection {
    @observable
    public artifacts: Array<Artifact> = [];
    public last_viewed?: number;
    private current_user_id: number;
    public families: Array<Family> = [];

    @computed
    public get preview_img(): string {
        if (this.artifacts.length > 0) {
            return this.artifacts[0].url;
        } else {
            return FALLBACK_COLLECTION_IMG;
        }
    }

    @computed
    public get caption(): string {
        if (this.artifacts.length > 0) {
            return this.artifacts[0].tagsPreview;
        }
        return '';
    }

    public constructor(args: IServerCollection, current_user_id: number) {
        super(args);

        assert(typeof current_user_id === 'number', 'invalid current_user_id parameter in collection constructor');
        this.current_user_id = current_user_id;
    }

    private static getSortedTagTitlesWithCount(collection: Collection): Array<ITagTitlesWithCount> {
        const artifactTags: Array<ISimplifiedTag> = ([] as any).concat.apply([], collection.artifacts.map(item => [...item.tags]).filter(item => item.length !== 0));

        const allArtifactTagTitles = artifactTags.map((obj: ISimplifiedTag) => obj.title);

        const tagWithCounts = _.countBy(allArtifactTagTitles, obj => obj);
        const allTagsWithCounts: Array<ITagTitlesWithCount> = Object.keys(tagWithCounts)
            .map(key => {
                return { tag: key, count: tagWithCounts[key] };
            })
            .filter(obj => obj.tag !== '');

        const sortedWithCounts: Array<ITagTitlesWithCount> = allTagsWithCounts
            .sort((a, b) => {
                return a.count - b.count;
            })
            .reverse();

        return sortedWithCounts;
    }

    public static getTagPreview(collection: Collection): string {
        let tagPreview = '';
        const sortedWithCounts: Array<ITagTitlesWithCount> = this.getSortedTagTitlesWithCount(collection);
        sortedWithCounts.forEach((obj, index) => {
            if (index !== sortedWithCounts.length - 1) {
                tagPreview += obj.tag + ', ';
            } else {
                tagPreview += obj.tag;
            }
        });

        return tagPreview;
    }

    public getMinimumViewableDate(): moment.Moment {
        if (this.user_id === this.current_user_id) {
            return this.created_at;
        }

        let min = moment();
        this.families.forEach(family => {
            const userJoined = family.getUserJoinDate(this.current_user_id);
            const collectionAdded = family.getCollectionAddDate(this.id);
            const viewableDate = Math.max(userJoined.unix(), collectionAdded.unix());
            if (viewableDate < min.unix()) {
                min = moment.unix(viewableDate);
            }
        });
        return min;
    }

    public getArtifactAddDate(artifact_id: number) {
        assert(this.artifactInfoMap.has(artifact_id), 'Collection#getArtifactAddDate - requested unknown artifact_id: ' + artifact_id);
        const artifactInfo = this.artifactInfoMap.get(artifact_id);
        return moment(artifactInfo!.added_at);
    }

    @computed
    private get totalArtifactNotifications(): number {
        console.assert(this.artifacts, 'Collection#getTotalArtifactNotifications artifacts is undefined');

        return this.artifacts.reduce((accumulator, artifact) => {
            return accumulator + artifact.notificationsCount;
        }, 0);
    }

    public getNewArtifactIds(): Array<number> {
        console.assert(this.artifacts_info, 'Collection#getNewArtifactIds artifacts_info is undefined');
        const out: Array<number> = [];

        let startDate: moment.Moment;
        if (this.last_viewed) {
            startDate = moment(this.last_viewed);
        } else {
            startDate = this.getMinimumViewableDate();
        }
        console.assert(startDate, 'Collection#getNewArtifactIds startDate is undefined');

        this.artifacts_info.forEach((artifact_info: IArtifactInfo) => {
            if (this.current_user_id !== artifact_info.user_id && moment(artifact_info.added_at) > startDate) {
                out.push(artifact_info.artifact_id);
            }
        });

        return out;
    }

    @computed
    public get notificationsCount(): number {
        let count = 0;
        count += this.getNewArtifactIds().length;
        count += this.totalArtifactNotifications;
        return count;
    }
}



// WEBPACK FOOTER //
// ./src/_interfaces/Collection.ts